import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Skeleton,
  Snackbar,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { compactToggleXsSx } from "@aeriosiq/ui";
import {
  DEFECT_SEVERITIES,
  DEFECT_STATUSES,
  type Defect,
  type DefectSeverity,
  type DefectStatus,
} from "@aeriosiq/types";
import {
  useDefectsQuery,
  useUndoableSoftDeleteDefectMutation,
  useUndoableUpdateDefectMutation,
} from "./queries.js";
import { useImageActivationStore } from "../analysis/state/imageActivationStore.js";
import {
  applyFilters,
  useAnalysisFilterStore,
} from "../analysis/state/analysisFilterStore.js";
import { useAnalysisFilter } from "../analysis/useAnalysisFilter.js";
import {
  denyReasonTooltip,
  type DefectEditPermission,
} from "../analysis/editPermission.js";
// SB6-ANA-PROG-01 — grading gate. Severity/status edits + the merge
// shortcut stay disabled until the dataset is `dataPointReady`.
import {
  GRADING_NOT_READY_TOOLTIP,
  isGradingReady,
  useGradingReady,
} from "../analysis/state/analysisReadinessStore.js";
import { CataloguePopover, useAiSharingGuard } from "../catalogues/index.js";
import { useEffectiveClassModel } from "../catalogues/useEffectiveClassModel.js";
import { useSelectionStore } from "../analysis/state/selectionStore.js";
import { useMe } from "@aeriosiq/auth-client";
import { DefectsTable } from "./DefectsTable.js";
import { useDefectDensity, type DefectDensity } from "./useDefectDensity.js";
import { mutationErrorToSnack } from "./mutationError.js";
import { gradeToSeverity } from "./severityDisplay.js";
import { LayoutGridIcon, Rows2Icon, Rows3Icon } from "@aeriosiq/ui/icons";

interface Props {
  inspectionId: string;
  onSelect?: (defect: Defect) => void;
  /**
   * Story 008 AC7–AC10 — D-5 role/phase gate. When omitted the panel
   * stays writeable (legacy / unit-test default).
   */
  editPermission?: DefectEditPermission;
  /**
   * R58-L-07 AC2/AC3 — when set, scopes the defects query to a single
   * image.
   */
  imageIdScope?: string | null;
  /**
   * R58-L-07 AC1 — when true, the panel renders flush (no inner
   * `overflow: auto`) so the parent ListWidget body owns the only
   * scroll container.
   */
  scrollFromParent?: boolean;
  /**
   * R58-L-07 AC4 — count badge feed-up.
   */
  onCountChange?: (n: number) => void;
  /**
   * SB6-ANA-LIST-02 — when the explicit string `"none"` (or empty
   * string) is passed, the Severity column is hidden entirely (per
   * OQ-SB6-C5: severity has no meaning without a class model). When
   * `null` / undefined (the direct-mount/test default), the column
   * stays visible so the existing severity-pill specs keep finding
   * their target.
   */
  activeClassModelId?: string | null;
  /**
   * SB6-ANA-LIST-02 — set of image ids the current user has reviewed
   * via `image_review_states`. Check column reflects this set.
   */
  viewedImageIds?: ReadonlySet<string>;
}

interface DefectsHeaderProps {
  count: number;
  status: DefectStatus[];
  onStatusChange: (next: DefectStatus[]) => void;
  density: DefectDensity;
  onDensityChange: (next: DefectDensity) => void;
}

function DefectsHeader({
  count,
  status,
  onStatusChange,
  density,
  onDensityChange,
}: DefectsHeaderProps) {
  // Three-state cycle compact → normal → large → compact. A single
  // click both advances and exposes the current value (the icon swaps),
  // mirroring the Turbines-page DensityToggleButton extended for the
  // third option per `.vibe/12_STORY_BOOK_6_FIXES.md` §C1.Q4.
  const nextDensity: DefectDensity =
    density === "compact"
      ? "normal"
      : density === "normal"
        ? "large"
        : "compact";
  const Icon =
    density === "compact"
      ? Rows3Icon
      : density === "normal"
        ? LayoutGridIcon
        : Rows2Icon;
  const tooltip = `Density: ${density} (click for ${nextDensity})`;
  return (
    <Stack
      direction="row"
      spacing={2}
      sx={{
        px: 2,
        py: 1,
        alignItems: "center",
        borderBottom: 1,
        borderColor: "divider",
      }}
    >
      {/* UI-01 follow-up (2026-06-05) — subtitle2, same scale as the
          Findings header + the widget's "Analysis" title. */}
      <Typography variant="subtitle2" component="h2" sx={{ flex: 1 }}>
        Defects ({count})
      </Typography>
      {/* UI-01 follow-up (Christian, 2026-06-05) — the status multiselect
          Autocomplete hid the options behind a dropdown; like the AI tab the
          user should SEE all states at once. Same XS segmented control, but
          NON-exclusive: defect status filtering stays multi-select (the AI
          tab's `state` is a single-select axis, defect `status[]` is not).
          Empty selection keeps today's Autocomplete-cleared semantics. */}
      <ToggleButtonGroup
        size="small"
        value={[...status]}
        onChange={(_e, value: DefectStatus[]) => {
          onStatusChange(value);
        }}
        aria-label="Defect status filter"
        data-testid="defects-status-filter"
      >
        {DEFECT_STATUSES.map((s) => (
          <ToggleButton key={s} value={s} aria-label={s} sx={compactToggleXsSx}>
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      <Tooltip title={tooltip}>
        <IconButton
          size="small"
          onClick={() => {
            onDensityChange(nextDensity);
          }}
          aria-label={`Defect row density (currently ${density})`}
          data-testid="defects-density-toggle"
          data-density={density}
        >
          <Icon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
  );
}

export function DefectsPanel({
  inspectionId,
  onSelect,
  editPermission,
  imageIdScope,
  scrollFromParent,
  onCountChange,
  activeClassModelId = null,
  viewedImageIds,
}: Props) {
  const [status, setStatus] = useState<DefectStatus[]>([...DEFECT_STATUSES]);
  // SB6-ANA-PROG-01 — AND the readiness gate with the D-5 permission gate.
  const gradingReady = useGradingReady();
  const permissionAllowed = editPermission?.allowed !== false;
  const canEdit = permissionAllowed && gradingReady;
  const denyTooltip =
    editPermission && !editPermission.allowed
      ? denyReasonTooltip(editPermission.reason)
      : !gradingReady && permissionAllowed
        ? GRADING_NOT_READY_TOOLTIP
        : null;

  const filterQ = useAnalysisFilterStore((s) => s.q);
  const { defectClassIds } = useAnalysisFilter();
  const query = useDefectsQuery(inspectionId, {
    status,
    pageSize: scrollFromParent ? 2000 : 200,
    ...(imageIdScope ? { imageId: imageIdScope } : {}),
    ...(filterQ ? { q: filterQ } : {}),
    ...(defectClassIds.length > 0
      ? { defectClassIds: [...defectClassIds] }
      : {}),
  });
  const allItems = useMemo<Defect[]>(
    () => query.data?.items ?? [],
    [query.data?.items],
  );

  const blades = useAnalysisFilterStore((s) => s.blades);
  const sides = useAnalysisFilterStore((s) => s.sides);
  const severities = useAnalysisFilterStore((s) => s.severities);
  const statuses = useAnalysisFilterStore((s) => s.statuses);
  const items = useMemo<Defect[]>(() => {
    const projected = allItems.map((d) => ({
      ...d,
      bladeId: d.bladeId ?? null,
      side: d.side ?? null,
      classCategory: d.classCategory ?? null,
    }));
    return applyFilters(projected, {
      blades,
      sides,
      severities,
      statuses,
    }) as Defect[];
  }, [allItems, blades, sides, severities, statuses]);

  const total = query.data?.total ?? 0;

  useEffect(() => {
    if (onCountChange) onCountChange(total);
  }, [onCountChange, total]);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const externalActiveId = useSelectionStore((s) => s.activeFindingId);
  const externalActiveKind = useSelectionStore((s) => s.activeKind);
  useEffect(() => {
    if (externalActiveKind !== "defect") return;
    if (externalActiveId && externalActiveId !== selectedId) {
      setSelectedId(externalActiveId);
    }
  }, [externalActiveId, externalActiveKind, selectedId]);
  const update = useUndoableUpdateDefectMutation(inspectionId);
  // UI-01 follow-up (Christian, 2026-06-05) — soft-remove from the row
  // context menu. Same undoable wrapper the viewer's test bridge uses:
  // pushes onto the undo stack (Ctrl+Z restores) + appends an audit row.
  const softDelete = useUndoableSoftDeleteDefectMutation(inspectionId);
  const activeImageId = useImageActivationStore((s) => s.activeImageId);

  // SB6-ANA-LIST-02 — per-user density preference persisted to
  // localStorage; falls back to "anonymous" so unit tests without auth
  // still have a stable key.
  const me = useMe();
  const userId = me.data?.id ?? "anonymous";
  const { density, setDensity } = useDefectDensity(userId);

  const [editorAnchor, setEditorAnchor] = useState<HTMLElement | null>(null);
  const [editorKind, setEditorKind] = useState<"status" | "severity" | null>(
    null,
  );
  const [editorDefectId, setEditorDefectId] = useState<string | null>(null);

  const [ctxMenu, setCtxMenu] = useState<{
    anchorPos: { left: number; top: number };
    rowEl: HTMLElement;
    defectId: string;
  } | null>(null);
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);
  const [popoverSelection, setPopoverSelection] = useState<readonly string[]>(
    [],
  );
  const selectedFindingIds = useSelectionStore((s) => s.selectedFindingIds);
  const ctxAiGuard = useAiSharingGuard(); // FOLLOWUP(story-067)
  void ctxAiGuard;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const isMod = e.ctrlKey || e.metaKey;
      if (!isMod || e.key.toLowerCase() !== "k") return;
      const targetIds = Array.from(selectedFindingIds);
      const defectsHere = items
        .filter((d) => targetIds.includes(d.id))
        .map((d) => d.id);
      const first = defectsHere[0];
      if (!first) return;
      if (!canEdit) return;
      e.preventDefault();
      const anchor = document.querySelector(`[data-defect-id="${first}"]`);
      if (anchor instanceof HTMLElement) {
        setPopoverAnchor(anchor);
        setPopoverSelection(defectsHere);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  }, [selectedFindingIds, items, canEdit]);

  const [snack, setSnack] = useState<
    | { kind: "success"; summary: string }
    | { kind: "error"; errorId: string; message?: string }
    | null
  >(null);

  const closeEditor = () => {
    setEditorAnchor(null);
    setEditorKind(null);
    setEditorDefectId(null);
  };

  const openStatusEditor = (
    e: React.MouseEvent<HTMLElement>,
    defectId: string,
  ) => {
    e.stopPropagation();
    setEditorAnchor(e.currentTarget);
    setEditorKind("status");
    setEditorDefectId(defectId);
  };
  const openSeverityEditor = (
    e: React.MouseEvent<HTMLElement>,
    defectId: string,
  ) => {
    e.stopPropagation();
    setEditorAnchor(e.currentTarget);
    setEditorKind("severity");
    setEditorDefectId(defectId);
  };

  const handlePick = (
    next: DefectStatus | DefectSeverity,
    displayLabel?: string,
  ) => {
    const defectId = editorDefectId;
    const kind = editorKind;
    closeEditor();
    if (!defectId || !kind) return;
    // SB6-ANA-PROG-01 — gate the grading edit at the action layer.
    if (!isGradingReady()) return;
    const current = items.find((d) => d.id === defectId);
    const body =
      kind === "status"
        ? { status: next as DefectStatus }
        : { severity: next as DefectSeverity };
    // 8.0.0 — the read-side severity is nullable ("not graded"); the undo
    // snapshot records the honest null so Ctrl+Z knows the defect was
    // previously ungraded.
    const before: {
      status?: DefectStatus;
      severity?: DefectSeverity | null;
    } = {};
    const after: {
      status?: DefectStatus;
      severity?: DefectSeverity | null;
    } = {};
    if (kind === "status") {
      if (current?.status !== undefined) before.status = current.status;
      after.status = next as DefectStatus;
    } else {
      if (current?.severity !== undefined) before.severity = current.severity;
      after.severity = next as DefectSeverity;
    }
    update.mutate(
      {
        defectId,
        body,
        before,
        after,
        dataAcquisitionId: inspectionId,
        imageId: activeImageId ?? current?.imageId ?? "",
      },
      {
        onSuccess: () => {
          const summary =
            kind === "status"
              ? `status: ${next}`
              : `severity: ${displayLabel ?? next}`;
          setSnack({ kind: "success", summary });
        },
        onError: (err) => {
          setSnack(mutationErrorToSnack(err));
        },
      },
    );
  };

  // UX Q6 (severity story, 8.0.0) — the severity editor speaks the
  // class-model's own grade vocabulary. A grade pick maps onto the
  // (unchanged, non-nullable) request-side enum RELATIVE to the model —
  // the exact mirror of the gateway's read-side mapping — so the round
  // trip is stable.
  const effectiveClassModel = useEffectiveClassModel(activeClassModelId);
  const handlePickGrade = (classCategory: number) => {
    const severity = gradeToSeverity(
      classCategory,
      effectiveClassModel.classModel?.classCount ?? null,
    );
    if (!severity) return;
    handlePick(severity, `grade ${String(classCategory)}`);
  };

  const isError = query.isError;
  const errorId =
    (query.error as { errorId?: string } | undefined)?.errorId ?? "unknown";
  const isPending = query.isPending;
  const isEmpty = !isPending && !isError && items.length === 0;

  // SB6-ANA-LIST-02 — severity column hidden entirely when the DA has
  // no class model (OQ-SB6-C5). Direct-mount paths pass `null` (default)
  // and keep the column visible so existing severity-pill specs work.
  // Severity story (8.0.0) — the resolved "None" model (class_count === 0,
  // detected via the class-models list, never a hard-coded UUID) hides the
  // column too: in the live shell the prop carries the None model's UUID,
  // not the legacy "none" sentinel.
  const noClassModel =
    activeClassModelId === "" ||
    activeClassModelId === "none" ||
    effectiveClassModel.isNoneModel;
  const showSeverityColumn = !noClassModel;

  const tableBody = (
    <DefectsTable
      items={items}
      selectedId={selectedId}
      onRowClick={(d) => {
        setSelectedId(d.id);
        onSelect?.(d);
      }}
      onRowContextMenu={(e, defectId) => {
        setCtxMenu({
          anchorPos: { left: e.clientX, top: e.clientY },
          rowEl: e.currentTarget,
          defectId,
        });
      }}
      onOpenStatusEditor={openStatusEditor}
      onOpenSeverityEditor={openSeverityEditor}
      inspectionId={inspectionId}
      {...(editPermission ? { editPermission } : {})}
      showSeverityColumn={showSeverityColumn}
      {...(viewedImageIds ? { viewedImageIds } : {})}
      density={density}
    />
  );

  return (
    <Paper
      variant="outlined"
      role="region"
      aria-label="Defects"
      sx={
        scrollFromParent
          ? {
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              height: "100%",
            }
          : { overflow: "hidden" }
      }
    >
      <DefectsHeader
        count={total}
        status={status}
        onStatusChange={setStatus}
        density={density}
        onDensityChange={setDensity}
      />

      {isPending ? (
        <Stack spacing={1} sx={{ p: 2 }}>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} variant="rectangular" height={56} />
          ))}
        </Stack>
      ) : isError ? (
        <Stack
          spacing={1}
          sx={{ p: 3 }}
          role="alert"
          aria-live="assertive"
          aria-atomic="true"
        >
          <Typography variant="subtitle1" component="h3">
            Couldn&apos;t load defects
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontFamily: "monospace" }}
          >
            errorId: {errorId}
          </Typography>
          <Box>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                void query.refetch();
              }}
            >
              Retry
            </Button>
          </Box>
        </Stack>
      ) : isEmpty ? (
        <Box sx={{ p: 3 }}>
          <Typography color="text.secondary">No defects yet</Typography>
        </Box>
      ) : scrollFromParent ? (
        // SB6-ANA-LIST-02 — DataTable in the live shell. Sticky-header
        // keeps column titles visible while the user scrolls; the panel
        // owns the only scroll container.
        <Box
          data-testid="defects-virtual-list"
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: "1 1 auto",
            minHeight: 0,
            overflow: "auto",
          }}
        >
          {tableBody}
        </Box>
      ) : (
        <Box sx={{ maxHeight: 480, overflowY: "auto" }}>{tableBody}</Box>
      )}

      <Menu
        anchorEl={editorAnchor}
        open={Boolean(editorAnchor)}
        onClose={closeEditor}
      >
        {editorKind === "status"
          ? DEFECT_STATUSES.map((s) => (
              <MenuItem
                key={s}
                onClick={() => {
                  handlePick(s);
                }}
              >
                {s}
              </MenuItem>
            ))
          : editorKind === "severity"
            ? effectiveClassModel.grades.length > 0
              ? // UX Q6 — class-model grade labels are canonical: the menu
                // lists the model's own grades (number + colour swatch),
                // exactly the vocabulary the DefectDrawer's radios show.
                effectiveClassModel.grades.map((g) => (
                  <MenuItem
                    key={g.defectClassId}
                    data-grade={g.classCategory}
                    onClick={() => {
                      handlePickGrade(g.classCategory);
                    }}
                  >
                    <Box
                      component="span"
                      aria-hidden="true"
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        mr: 1,
                        display: "inline-block",
                        bgcolor: g.colorCode ?? "text.disabled",
                      }}
                    />
                    {String(g.classCategory)}
                  </MenuItem>
                ))
              : // Legacy fallback — no class-model context resolvable
                // (direct/test mounts): keep the contract-enum menu.
                DEFECT_SEVERITIES.map((s) => (
                  <MenuItem
                    key={s}
                    onClick={() => {
                      handlePick(s);
                    }}
                  >
                    {s}
                  </MenuItem>
                ))
            : null}
      </Menu>

      {snack ? (
        <Snackbar
          open
          autoHideDuration={snack.kind === "error" ? 6000 : 3000}
          onClose={() => {
            setSnack(null);
          }}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          {snack.kind === "success" ? (
            <Alert
              severity="success"
              variant="filled"
              onClose={() => {
                setSnack(null);
              }}
            >
              Defect updated — {snack.summary}
            </Alert>
          ) : (
            <Alert
              severity="error"
              variant="filled"
              onClose={() => {
                setSnack(null);
              }}
              data-testid="defect-mutation-error"
            >
              {/* ANA-R1-01 (AC3) — surface the BE error message + a REAL
                  correlatable id (the lyse/v1 envelope `code`/`errorId`),
                  never the bare "error id unknown". */}
              {snack.message
                ? `${snack.message} (errorId: ${snack.errorId})`
                : `Update failed (errorId: ${snack.errorId})`}
            </Alert>
          )}
        </Snackbar>
      ) : null}

      <Menu
        open={Boolean(ctxMenu)}
        onClose={() => {
          setCtxMenu(null);
        }}
        anchorReference="anchorPosition"
        {...(ctxMenu ? { anchorPosition: ctxMenu.anchorPos } : {})}
      >
        <Tooltip
          title={denyTooltip ?? ""}
          disableHoverListener={canEdit}
          disableFocusListener={canEdit}
          disableTouchListener={canEdit}
          placement="left"
        >
          <Box component="span">
            <MenuItem
              disabled={!canEdit}
              aria-disabled={!canEdit}
              onClick={() => {
                if (!canEdit) return;
                if (!ctxMenu) return;
                const { rowEl, defectId } = ctxMenu;
                setPopoverAnchor(rowEl);
                setPopoverSelection([defectId]);
                setCtxMenu(null);
              }}
            >
              Reclassify
            </MenuItem>
          </Box>
        </Tooltip>
        {/* UI-01 follow-up (Christian, 2026-06-05) — there was no way to
            remove a defect from the list. Soft delete (server keeps the
            row, `deleted_at` set) via the undoable wrapper: Ctrl+Z
            restores; an audit-log row is appended. */}
        <Tooltip
          title={denyTooltip ?? ""}
          disableHoverListener={canEdit}
          disableFocusListener={canEdit}
          disableTouchListener={canEdit}
          placement="left"
        >
          <Box component="span">
            <MenuItem
              disabled={!canEdit || softDelete.isPending}
              aria-disabled={!canEdit}
              data-testid="defect-ctx-remove"
              sx={{ color: "error.main" }}
              onClick={() => {
                if (!canEdit || !ctxMenu) return;
                if (!isGradingReady()) return;
                const defect = items.find((d) => d.id === ctxMenu.defectId);
                setCtxMenu(null);
                if (!defect) return;
                softDelete.mutate(
                  {
                    defectId: defect.id,
                    defectSnapshot: defect,
                    dataAcquisitionId: inspectionId,
                    imageId: defect.imageId,
                  },
                  {
                    onSuccess: () => {
                      setSnack({
                        kind: "success",
                        summary: "Defect removed — Ctrl+Z to undo",
                      });
                    },
                    onError: (err) => {
                      setSnack(mutationErrorToSnack(err));
                    },
                  },
                );
              }}
            >
              Remove defect
            </MenuItem>
          </Box>
        </Tooltip>
      </Menu>

      <CataloguePopover
        open={Boolean(popoverAnchor)}
        anchorEl={popoverAnchor}
        onClose={() => {
          setPopoverAnchor(null);
          setPopoverSelection([]);
        }}
        inspectionId={inspectionId}
        source="defect"
        selection={{ ids: popoverSelection }}
        defectsOnInspection={items.map((d) => ({
          ...d,
          bladeId: d.bladeId ?? null,
          side: d.side ?? null,
          catalogueEntryId: d.catalogueEntryId,
        }))}
      />
    </Paper>
  );
}
